// src/hooks/useAuthPatched.js
// This module re-exports useAuth0 with E2E test mode support
// Import this instead of directly importing from @auth0/auth0-react

import { useAuth0 as useAuth0Original } from '@auth0/auth0-react';

// Mock user data for E2E testing
const mockUser = {
  sub: 'auth0|mock-user-id',
  email: 'e2e-test@example.com',
  email_verified: true,
  name: 'E2E Test User',
  nickname: 'e2e-test',
  picture: 'https://via.placeholder.com/150',
  updated_at: new Date().toISOString(),
};

// Mock auth context for E2E testing
const mockAuthContext = {
  isAuthenticated: true,
  isLoading: false,
  user: mockUser,
  loginWithRedirect: () => Promise.resolve(),
  loginWithPopup: () => Promise.resolve(),
  logout: () => {
    localStorage.removeItem('e2e_mock_auth_mode');
    window.location.href = '/';
  },
  getAccessTokenSilently: () => Promise.resolve('mock-access-token-for-e2e-tests'),
  getAccessTokenWithPopup: () => Promise.resolve('mock-access-token-for-e2e-tests'),
  getIdTokenClaims: () => Promise.resolve({
    __raw: 'mock-id-token',
    ...mockUser,
    iss: 'https://localhost/',
    aud: 'local-client-id',
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 86400,
  }),
  error: undefined,
};

// Check if E2E test mode is enabled (called on each hook invocation for reactivity)
const isE2ETestMode = () => {
  return (
    process.env.REACT_APP_E2E_TEST_MODE === 'true' ||
    (typeof window !== 'undefined' && localStorage.getItem('e2e_mock_auth_mode') === 'true')
  );
};

/**
 * Patched useAuth0 hook that returns mock data in E2E test mode.
 * This allows E2E tests to bypass Auth0 authentication entirely.
 */
export const useAuth0 = () => {
  const realAuth = useAuth0Original();

  // In E2E test mode, return mock auth data
  if (isE2ETestMode()) {
    return mockAuthContext;
  }

  return realAuth;
};

export default useAuth0;
